import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Users, FileText, Ticket, DollarSign, TrendingUp, MapPin, LayoutDashboard, Inbox, Images, ShieldAlert, CreditCard, Shield, SlidersHorizontal, Webhook, MailCheck } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import AdminInbox from '@/components/AdminInbox';
import AdminPayments from '@/components/AdminPayments';
import AdminSecurity from '@/components/AdminSecurity';
import AdminListingSettings from '@/components/AdminListingSettings';
// Review queue for PUBLISHED projects whose target date has already passed
// (audit follow-up #1). Nothing is deleted; the grace window is configurable.
import AdminExpiryQueue from '@/components/AdminExpiryQueue';
import ListingsModeration, { RoleAudit } from '@/components/AdminModeration';
import AdminCreateSubcontractor from '@/components/AdminCreateSubcontractor';
import BrowseRegressionPanel from '@/components/BrowseRegressionPanel';
import AdminCoverAudit from '@/components/AdminCoverAudit';
import AdminLeadCredits from '@/components/AdminLeadCredits';
import AdminConcurrencyProof from '@/components/AdminConcurrencyProof';
import AdminWebhookSetup from '@/components/AdminWebhookSetup';
import AdminPaymentRecovery from '@/components/AdminPaymentRecovery';
import AdminActivationChain from '@/components/AdminActivationChain';
import AdminOpsMonitor from '@/components/AdminOpsMonitor';

import AdminAdministrators from '@/components/AdminAdministrators';
import AdminAccessDenied from '@/components/AdminAccessDenied';
import AdminEmailAudit from '@/components/AdminEmailAudit';
import AdminEmailHealth from '@/components/AdminEmailHealth';
import AdminDnsAlignment from '@/components/AdminDnsAlignment';
import AdminGoDaddyGuide from '@/components/AdminGoDaddyGuide';
import AdminInboxTest from '@/components/AdminInboxTest';
import AdminDisputes from '@/components/AdminDisputes';
// Branding matrix (GIGGONE vs Sky Is The Limit Shop LLC) + one-click
// end-to-end payment acceptance test against the live webhook.
import AdminPaymentBranding from '@/components/AdminPaymentBranding';







import {
  money,
  tradeLabel,
  LEAD_PRICE,
  PLAN_CODES,
  PLAN_PRICES,
  PLAN_INCLUDED_LEADS,
  PLAN_TERM_MONTHS,
  planBillingSuffix,

} from '@/data/constants';

interface Row { [k: string]: any }

const Admin: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, loading } = useAuth();
  const [tab, setTab] = useState<'overview' | 'inbox' | 'payments' | 'webhook' | 'credits' | 'email' | 'security' | 'settings' | 'listings' | 'roles'>('overview');







  const [users, setUsers] = useState<Row[]>([]);
  const [listings, setListings] = useState<Row[]>([]);
  const [purchases, setPurchases] = useState<Row[]>([]);
  const [subs, setSubs] = useState<Row[]>([]);
  const [activity, setActivity] = useState<Row[]>([]);

  useEffect(() => {
    if (!profile?.is_admin) return;
    supabase.from('mkt_profiles').select('*').order('created_at', { ascending: false }).then(({ data }) => setUsers(data || []));
    supabase.from('mkt_listings').select('*').then(({ data }) => setListings(data || []));
    supabase.from('mkt_lead_purchases').select('*').then(({ data }) => setPurchases(data || []));
    supabase.from('mkt_subscriptions').select('*').eq('status', 'active').then(({ data }) => setSubs(data || []));
    supabase.from('mkt_activity').select('*').order('created_at', { ascending: false }).limit(25).then(({ data }) => setActivity(data || []));
  }, [profile?.is_admin]);


  if (loading) return <div className="py-24 text-center text-slate-500">{t('common.loading')}</div>;
  if (!profile) return <Navigate to={path('/login')} replace />;
  // Not an administrator: show WHICH account is signed in, its role and whether
  // the admin flag is set, instead of a dead-end sentence.
  if (!profile.is_admin) return <AdminAccessDenied />;


  const byType = (ty: string) => users.filter((u) => u.user_type === ty).length;
  const leadRevenue = purchases.reduce((s, p) => s + (p.amount || 0), 0);
  const subRevenue = subs.reduce((s, p) => s + (p.amount || 0), 0);
  const won = purchases.filter((p) => p.stage === 'won').length;
  const conversion = purchases.length ? Math.round((won / purchases.length) * 100) : 0;

  const tradeCounts: Record<string, number> = {};
  listings.forEach((l) => (tradeCounts[l.trade] = (tradeCounts[l.trade] || 0) + 1));
  const topTrades = Object.entries(tradeCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const cityCounts: Record<string, number> = {};
  listings.forEach((l) => {
    const k = `${l.city}, ${l.state}`;
    cityCounts[k] = (cityCounts[k] || 0) + 1;
  });
  const topCities = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const toggleStatus = async (u: Row) => {
    const next = u.status === 'suspended' ? 'approved' : 'suspended';
    await supabase.from('mkt_profiles').update({ status: next }).eq('id', u.id);
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, status: next } : x)));
  };

  const setUserLang = async (u: Row, l: string) => {
    await supabase.from('mkt_profiles').update({ preferred_language: l }).eq('id', u.id);
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, preferred_language: l } : x)));
  };

  // Lead and plan pricing is fixed platform-wide (see src/data/constants.ts) and
  // is intentionally NOT editable from the admin panel.


  const kpis = [
    { icon: Users, label: t('admin.homeowners'), value: byType('homeowner') },
    { icon: Users, label: t('admin.gcs'), value: byType('gc') },
    { icon: Users, label: t('admin.subs'), value: byType('subcontractor') },
    { icon: FileText, label: t('admin.projects'), value: listings.filter((l) => l.source_type === 'homeowner').length },
    { icon: FileText, label: t('admin.workOrders'), value: listings.filter((l) => l.source_type === 'gc').length },
    { icon: Ticket, label: t('admin.leadsSold'), value: purchases.length },
    { icon: DollarSign, label: t('admin.revenue'), value: money(leadRevenue + subRevenue) },
    { icon: TrendingUp, label: t('admin.mrr'), value: money(subRevenue) },
  ];

  const tabs = [
    { id: 'overview' as const, label: t('admin.tabOverview'), icon: LayoutDashboard },
    { id: 'inbox' as const, label: t('admin.tabInbox'), icon: Inbox },
    { id: 'payments' as const, label: t('admin.tabPayments'), icon: CreditCard },
    // Exact CRM webhook values + safe delivery test + payment recovery desk.
    { id: 'webhook' as const, label: lang === 'es' ? 'Webhook y recuperación' : 'Webhook & recovery', icon: Webhook },
    // Lead capacity (6 contractors max), sold-out alerts and credit reporting.

    { id: 'credits' as const, label: lang === 'es' ? 'Créditos y cupos' : 'Credits & capacity', icon: Ticket },
    // SPF / DKIM / DMARC audit, suppression list, transactional send log.
    { id: 'email' as const, label: lang === 'es' ? 'Entrega de correo' : 'Email delivery', icon: MailCheck },

    { id: 'security' as const, label: t('admin.tabSecurity'), icon: Shield },
    { id: 'settings' as const, label: t('admin.tabSettings'), icon: SlidersHorizontal },
    { id: 'listings' as const, label: t('admin.tabListings'), icon: Images },
    { id: 'roles' as const, label: t('admin.tabRoles'), icon: ShieldAlert },

  ];


  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Seo title={t('admin.title')} description={t('admin.title')} noindex />
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('admin.title')}</h1>

      <div className="mt-6 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
              tab === tb.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tb.icon className="h-4 w-4" /> {tb.label}
          </button>
        ))}
      </div>

      {tab === 'inbox' ? (
        <div className="mt-8">
          <AdminInbox />
        </div>
      ) : tab === 'payments' ? (
        <div className="mt-8">
          <AdminPayments />
          {/* Money reversed = entitlement revoked. Refund / chargeback cases,
              frozen credits, suspensions and the one-click reinstatement. */}
          <AdminDisputes />
          {/* Which payment surfaces can read GIGGONE vs must stay under the LLC,
              plus the end-to-end acceptance test (no card charged). */}
          <AdminPaymentBranding />
        </div>
      ) : tab === 'webhook' ? (
        <div className="mt-8">
          {/* Exact values to paste into the CRM + safe delivery test. */}
          <AdminWebhookSetup />
          {/* payment -> membership -> credits -> dashboard notice -> email,
              verified against the newest GENUINE sale (synthetic excluded). */}
          <AdminActivationChain />
          {/* Replay an already-collected payment. Never charges a card. */}
          <AdminPaymentRecovery />
        </div>

      ) : tab === 'credits' ? (

        <div className="mt-8">
          <AdminLeadCredits />
          {/* Durable proof that the credit + 6-contractor limits hold under load. */}
          <AdminConcurrencyProof />
        </div>
      ) : tab === 'email' ? (

        <div className="mt-8">
          {/* STEP 1 — the exact GoDaddy records (real DKIM key, ADD vs REPLACE). */}
          <AdminGoDaddyGuide />
          {/* LAUNCH BLOCKER: live SPF / DKIM / DMARC / Return-Path alignment gate
              and per-identity verification for support@ / noreply@. */}
          <AdminDnsAlignment />
          {/* Live SPF / DKIM / DMARC / alignment audit + provider scores. */}
          <AdminEmailAudit />
          {/* Real send to Gmail / Outlook seed boxes + the receiver's own SMTP answer. */}
          <AdminInboxTest />
          {/* Senders, templates, bounce webhook, suppression list, send log. */}
          <AdminEmailHealth />
        </div>


      ) : tab === 'security' ? (


        <div className="mt-8">
          {/* Live operations: error monitoring status, 12-category alert sweep,
              unacknowledged alerts. Runs hourly on cron, on demand here. */}
          <AdminOpsMonitor />
          <div className="mt-8">
            <AdminSecurity />
          </div>
          <BrowseRegressionPanel />
        </div>


      ) : tab === 'settings' ? (
        <div className="mt-8">
          <AdminListingSettings />
          {/* Published projects past their target date: confirm still active,
              or close as completed / cancelled. Nothing is ever deleted. */}
          <AdminExpiryQueue />
        </div>

      ) : tab === 'listings' ? (
        <div className="mt-8">
          <ListingsModeration />
          <AdminCoverAudit />
        </div>

      ) : tab === 'roles' ? (
        <div className="mt-8">
          {/* Who holds platform-administrator access (grant / revoke / pre-authorise). */}
          <AdminAdministrators />
          <RoleAudit />
          <AdminCreateSubcontractor />
        </div>


      ) : (


        <div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-5">
                <k.icon className="h-5 w-5 text-orange-500" />
                <p className="mt-3 text-2xl font-extrabold text-slate-900">{k.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('admin.conversion')}</h2>
              <p className="mt-3 text-4xl font-extrabold text-emerald-600">{conversion}%</p>
              <div className="mt-3 h-2 w-full rounded-full bg-slate-100">
                <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${conversion}%` }} />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('admin.topTrades')}</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {topTrades.map(([tr, c]) => (
                  <li key={tr} className="flex justify-between">
                    <span className="text-slate-600">{tradeLabel(tr, lang)}</span>
                    <span className="font-bold text-slate-900">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="flex items-center gap-1.5 text-sm font-extrabold uppercase tracking-wide text-slate-900">
                <MapPin className="h-4 w-4 text-slate-400" /> {t('admin.geo')}
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                {topCities.map(([c, n]) => (
                  <li key={c} className="flex justify-between">
                    <span className="text-slate-600">{c}</span>
                    <span className="font-bold text-slate-900">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.manageUsers')}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{t('common.name')}</th>
                  <th className="px-4 py-3">{t('common.email')}</th>
                  <th className="px-4 py-3">{t('common.type')}</th>
                  <th className="px-4 py-3">{t('auth.preferredLanguage')}</th>
                  <th className="px-4 py-3">{t('common.status')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {u.company_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600">{u.user_type}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.preferred_language}
                        onChange={(e) => setUserLang(u, e.target.value)}
                        className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold"
                      >
                        <option value="en">EN</option>
                        <option value="es">ES</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                          u.status === 'suspended' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {u.status === 'suspended' ? t('admin.suspended') : t('admin.approved')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleStatus(u)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        {u.status === 'suspended' ? t('admin.approve') : t('admin.suspend')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.managePricing')}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('pricing.payPerLead')}</p>
              <p className="mt-2 text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
              <p className="mt-1 text-xs text-slate-500">{t('pricing.fixedLeadNote')}</p>
            </div>
            {PLAN_CODES.map((code) => (
              <div key={code} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{code}</p>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">
                  {money(PLAN_PRICES[code])}
                  <span className="text-sm font-semibold text-slate-400">{planBillingSuffix(code, lang)}</span>
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {PLAN_INCLUDED_LEADS[code]} {t('dash.includedLeads')}
                  {PLAN_TERM_MONTHS[code] > 1 ? ` · ${PLAN_TERM_MONTHS[code]} mo` : ''}
                </p>
              </div>
            ))}
          </div>


          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.activity')}</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {activity.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">{t('common.none')}</p>
            ) : (
              activity.map((a) => (
                <div
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 text-sm last:border-0"
                >
                  <span className="font-semibold text-slate-800">{a.action}</span>
                  <span className="text-slate-500">{a.detail}</span>
                  <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
